import { useEffect, useState } from "react";
import { subscribeBadges, cachedGet } from "../lib/sharedData";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, FileSignature, Inbox, Calendar, Wallet,
  LogOut, Mailbox, Scale, ShieldCheck, Target, Plane, FolderOpen,
  ClipboardList, ClipboardCheck, Stethoscope, Activity, Plug, UsersRound, UserCircle, Key, FileBarChart, Lock,
  NotebookText, Bell, PanelLeftClose, PanelLeftOpen, BookUser, Sparkles, CalendarCheck, BrainCircuit, Trophy, Radar,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import WorkspaceSwitcher from "./WorkspaceSwitcher";

// Sidebar navigation. Top-level items render flat; the trailing "groups"
// (Paperwork, Settings) are rendered as collapsible sub-sections so the
// sidebar stays scannable as the platform grows. Each group has a header
// row + the sub-items underneath.
const AGENT_TOP = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", key: "1" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", key: "2" },
  { to: "/position-map", label: "Position map", icon: Target, testid: "nav-position-map", key: "P" },
  { to: "/inbox", label: "Unified inbox", icon: Mailbox, testid: "nav-inbox", key: "3", badge: "inbox" },
  { to: "/contacts", label: "Contacts", icon: BookUser, testid: "nav-contacts", key: "C" },
  { to: "/briefing", label: "Morning brief", icon: Sparkles, testid: "nav-briefing", key: "B" },
  { to: "/assistant", label: "AI Assistant", icon: BrainCircuit, testid: "nav-assistant", key: "X", badge: "assistant" },
  { to: "/agenda", label: "Agenda", icon: CalendarCheck, testid: "nav-agenda", key: "G", badge: "agenda" },
  { to: "/matches", label: "Matches", icon: Trophy, testid: "nav-matches", key: "W" },
  { to: "/clauses", label: "Clause Radar", icon: Radar, testid: "nav-clauses", key: "R" },
  { to: "/requests", label: "Requests", icon: ClipboardList, testid: "nav-requests", key: "Q" },
  { to: "/cases", label: "Cases", icon: Inbox, testid: "nav-cases", key: "4", badge: "cases" },
  { to: "/schedule", label: "Schedule", icon: Calendar, testid: "nav-schedule", key: "7" },
  { to: "/commissions", label: "Commissions", icon: Wallet, testid: "nav-commissions", key: "8" },
  { to: "/travel-planner", label: "Travel planner", icon: Plane, testid: "nav-travel-planner", key: "9" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports", key: "R" },
  { to: "/settings/team", label: "Team", icon: UsersRound, testid: "nav-team", key: "T" },
];

const PAPERWORK_GROUP = {
  key: "paperwork",
  label: "Paperwork",
  items: [
    { to: "/legal", label: "Legal", icon: Scale, testid: "nav-legal", key: "6", badge: "legal" },
    { to: "/contracts", label: "Contracts", icon: FileSignature, testid: "nav-contracts", key: "5", badge: "contracts" },
    { to: "/mandates", label: "Mandates", icon: Key, testid: "nav-mandates", key: "M", badge: "mandates" },
    { to: "/notes", label: "Notes", icon: NotebookText, testid: "nav-notes", key: "N" },
  ],
};

const SETTINGS_GROUP = {
  key: "settings",
  label: "Settings",
  items: [
    { to: "/settings/permissions", label: "Permissions", icon: Lock, testid: "nav-permissions", key: "L" },
    { to: "/settings/profile", label: "Profile & Privacy", icon: UserCircle, testid: "nav-profile", key: "Y" },
    { to: "/settings/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance", key: "0", badge: "compliance" },
    { to: "/settings/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations", key: "I" },
  ],
};

// Admin gets the same shape, with the audit log added to the Settings group.
const ADMIN_TOP = [...AGENT_TOP];

const ADMIN_SETTINGS_GROUP = {
  key: "settings",
  label: "Settings",
  items: [
    ...SETTINGS_GROUP.items,
    { to: "/audit-log", label: "Audit log", icon: ShieldCheck, testid: "nav-audit-log", key: "A" },
  ],
};

const AGENT_NAV = {
  top: AGENT_TOP,
  groups: [PAPERWORK_GROUP, SETTINGS_GROUP],
};

const ADMIN_NAV = {
  top: ADMIN_TOP,
  groups: [PAPERWORK_GROUP, ADMIN_SETTINGS_GROUP],
};

const TD_NAV = {
  top: [
    { to: "/td-home", label: "Club home", icon: LayoutDashboard, testid: "nav-td-home", key: "1" },
    { to: "/position-map", label: "Position map", icon: Target, testid: "nav-position-map", key: "2" },
    { to: "/inbox", label: "Inbox", icon: Mailbox, testid: "nav-inbox", key: "3", badge: "inbox" },
    { to: "/cases", label: "Cases", icon: Inbox, testid: "nav-cases", key: "4", badge: "cases" },
    { to: "/scouting-docs", label: "Scouting docs", icon: FolderOpen, testid: "nav-scouting-docs", key: "5" },
    { to: "/club-staff", label: "Club staff", icon: Users, testid: "nav-club-staff", key: "6" },
  ],
  groups: [
    {
      key: "settings",
      label: "Settings",
      items: [
        { to: "/settings/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations", key: "I" },
        { to: "/settings/team", label: "Team", icon: UsersRound, testid: "nav-team-td", key: "T" },
      ],
    },
  ],
};

const SCOUTING_NAV = {
  top: [
    { to: "/scouting-home", label: "Scouting home", icon: LayoutDashboard, testid: "nav-scouting-home", key: "1" },
    { to: "/position-map", label: "Position map", icon: Target, testid: "nav-position-map", key: "2" },
    { to: "/scouting-docs", label: "Shared docs", icon: FolderOpen, testid: "nav-scouting-docs", key: "3" },
    { to: "/inbox", label: "Inbox", icon: Mailbox, testid: "nav-inbox", key: "4", badge: "inbox" },
  ],
  groups: [],
};

const STAFF_NAV = {
  top: [
    { to: "/staff-home", label: "Staff home", icon: LayoutDashboard, testid: "nav-staff-home", key: "1" },
    { to: "/inbox", label: "Inbox", icon: Mailbox, testid: "nav-inbox", key: "2", badge: "inbox" },
    { to: "/scouting-docs", label: "Shared docs", icon: FolderOpen, testid: "nav-scouting-docs", key: "3" },
  ],
  groups: [],
};

const CLIENT_NAV = {
  top: [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "nav-dashboard", key: "1" },
    { to: "/inbox", label: "Inbox", icon: Mailbox, testid: "nav-inbox", key: "2", badge: "inbox" },
    { to: "/schedule", label: "Schedule", icon: Calendar, testid: "nav-schedule", key: "3" },
  ],
  groups: [],
};

function navFor(role) {
  if (role === "tech_director") return TD_NAV;
  if (role === "client") return CLIENT_NAV;
  if (role === "head_scout" || role === "scout") return SCOUTING_NAV;
  if (role === "head_coach" || role === "asst_coach" || role === "club_doctor" || role === "physio") return STAFF_NAV;
  if (role === "admin") return ADMIN_NAV;
  return AGENT_NAV;
}

function suiteLabelFor(role) {
  if (role === "tech_director") return "Club Operations";
  if (role === "client") return "Player Hub";
  if (role === "head_scout" || role === "scout") return "Scouting Suite";
  if (role === "head_coach" || role === "asst_coach") return "Coaching Staff";
  if (role === "club_doctor" || role === "physio") return "Medical Staff";
  return "Football Operations";
}

const TONE_STYLES = {
  red: "bg-red-500 text-white shadow-[0_0_0_2px_rgba(239,68,68,0.3)]",
  amber: "bg-amber-400 text-amber-950 shadow-[0_0_0_2px_rgba(251,191,36,0.3)]",
  green: "bg-emerald-500 text-white shadow-[0_0_0_2px_rgba(16,185,129,0.3)]",
};

function Badge({ count, tone, collapsed }) {
  if (!tone && !count) return null;
  const cls = TONE_STYLES[tone] || TONE_STYLES.green;
  if (collapsed) {
    // Tiny dot in the collapsed rail — anchored to icon top-right.
    return (
      <span
        className={`absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-black flex items-center justify-center ${cls}`}
        data-testid="sidebar-badge-dot"
      >
        {count > 9 ? "9+" : (count || "")}
      </span>
    );
  }
  return (
    <span
      className={`ml-auto px-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-black flex items-center justify-center ${cls}`}
      data-testid="sidebar-badge"
    >
      {count > 99 ? "99+" : (count || "•")}
    </span>
  );
}

function NavItem({ item, collapsed, badges, loc }) {
  const Icon = item.icon;
  const active = loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
  const badge = item.badge ? badges[item.badge] : null;
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid}
      title={collapsed ? item.label : undefined}
      className={`pm-sidebar-link ${active ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}
    >
      <span className={`relative ${collapsed ? "" : "flex items-center gap-3 flex-1 min-w-0"}`}>
        <span className="relative inline-flex">
          <Icon size={18} strokeWidth={1.75} />
          {collapsed && badge && <Badge count={badge.count} tone={badge.tone} collapsed />}
        </span>
        {!collapsed && <span className="truncate">{item.label}</span>}
      </span>
      {!collapsed && (
        badge ? <Badge count={badge.count} tone={badge.tone} />
          : <kbd className="ml-auto text-[9px] font-mono-tab opacity-50">{item.key}</kbd>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const NAV = navFor(user?.role);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pm_sidebar_collapsed") === "1");
  const [badges, setBadges] = useState({});
  const [activeWs, setActiveWs] = useState(null);
  useEffect(() => {
    if (!user) return;
    cachedGet("/workspaces/mine", 5 * 60000).then((list) => {
      const stored = localStorage.getItem("pm_active_workspace_id");
      setActiveWs((list || []).find((w) => w.id === stored) || (list || []).find((w) => w.is_primary) || (list || [])[0] || null);
    }).catch(() => {});
  }, [user, loc.pathname]);
  const logoSrc = activeWs?.logo_url ? (activeWs.logo_url.startsWith("http") ? activeWs.logo_url : `${process.env.REACT_APP_BACKEND_URL}${activeWs.logo_url}`) : null;
  // Track which nav groups are open. Default = all open. Persisted to local
  // storage so the user's preference survives a reload.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("pm_sidebar_groups") || "{}");
      return stored;
    } catch {
      return {};
    }
  });
  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: prev[key] === false ? true : false };
      localStorage.setItem("pm_sidebar_groups", JSON.stringify(next));
      return next;
    });
  };
  const groupIsOpen = (group) => {
    // A group is open by default unless explicitly stored as false. We also
    // auto-open the group when the user is currently navigated to one of
    // its items, so they never end up looking at a collapsed parent of
    // their active route.
    const explicit = openGroups[group.key];
    if (explicit === false) {
      const onCurrent = group.items.some((it) =>
        loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"),
      );
      return onCurrent;
    }
    return true;
  };

  // Auto-close the mobile drawer whenever the route changes.
  useEffect(() => {
    if (mobileOpen) onMobileClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  // Persist collapsed state across reloads, and expose a `[` keyboard
  // shortcut for power users (avoids the mouse trip to the top of the
  // sidebar every time they want more width).
  useEffect(() => {
    localStorage.setItem("pm_sidebar_collapsed", collapsed ? "1" : "0");
    document.documentElement.style.setProperty("--pm-sidebar-w", collapsed ? "64px" : "288px");
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key === "[") {
        e.preventDefault();
        setCollapsed((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Light badge polling — keep the UI responsive without hammering the
  // backend (every 45s).
  useEffect(() => subscribeBadges((b) => setBadges(b || {})), []);

  return (
    <>
      {/* Mobile backdrop — only renders when drawer is open below lg breakpoint */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onMobileClose}
          data-testid="sidebar-mobile-backdrop"
        />
      )}
      <aside
        className={`flex flex-col shrink-0 h-screen text-slate-300 transition-all duration-200 ease-out
          lg:sticky lg:top-0 lg:translate-x-0 lg:${collapsed ? "w-16" : "w-72"}
          fixed top-0 left-0 z-50 w-72 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          ${collapsed ? "lg:w-16" : "lg:w-72"}
        `}
        style={{ background: "var(--pm-navy)", borderRight: "1px solid var(--pm-border-dark)" }}
        data-testid="sidebar" data-no-translate
        data-collapsed={collapsed ? "1" : "0"}
        data-mobile-open={mobileOpen ? "1" : "0"}
      >
      <div className={`pt-6 pb-5 flex items-center ${collapsed ? "px-3 justify-center" : "px-6 gap-3"}`}>
        {collapsed ? (
          // When collapsed, the logo IS the expand button — single visible
          // affordance so the user always knows how to widen the rail back
          // out. Keyboard `[` still toggles it for power users.
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg shrink-0 transition-transform hover:scale-105 relative group"
            style={{ background: "var(--pm-green)", color: "#062E17" }}
            title="Expand sidebar ( [ )"
            data-testid="sidebar-expand-btn"
            aria-label="Expand sidebar"
          >
            P
            <span className="absolute -right-1.5 -bottom-1.5 w-5 h-5 rounded-full bg-white shadow-md flex items-center justify-center text-slate-700 opacity-90 group-hover:opacity-100">
              <PanelLeftOpen size={11} />
            </span>
          </button>
        ) : logoSrc ? (
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0" data-testid="sidebar-agency-logo">
            <img src={logoSrc} alt={activeWs?.name || "Agency"} className="w-full h-full object-contain" />
          </div>
        ) : (
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg shrink-0"
            style={{ background: "var(--pm-green)", color: "#062E17" }}
          >
            P
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-xl text-white tracking-tight truncate" data-testid="sidebar-brand">{activeWs?.logo_url ? activeWs.name : "Playmaker"}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold truncate">
              {suiteLabelFor(user?.role)}
            </div>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
            title="Collapse sidebar ( [ )"
            data-testid="sidebar-collapse-btn"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      {!collapsed && (user?.role === "agent" || user?.role === "admin" || user?.role === "tech_director") && (
        <WorkspaceSwitcher />
      )}

      <nav className={`mt-2 flex-1 space-y-1 pm-scroll overflow-y-auto ${collapsed ? "px-2" : "px-3"}`}>
        {!collapsed && (
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Navigation</div>
        )}
        {(NAV.top || []).map((item) => (
          <NavItem
            key={item.to}
            item={item}
            collapsed={collapsed}
            badges={badges}
            loc={loc}
          />
        ))}

        {/* Collapsible groups: Paperwork, Settings */}
        {(NAV.groups || []).map((group) => {
          const open = groupIsOpen(group);
          return (
            <div key={group.key} className="pt-3">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="w-full px-3 py-1 flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-300 font-bold rounded transition-colors"
                  data-testid={`nav-group-toggle-${group.key}`}
                  aria-expanded={open}
                >
                  <span className={`transition-transform inline-block ${open ? "rotate-90" : ""}`}>›</span>
                  {group.label}
                </button>
              )}
              {(collapsed || open) && (
                <div className={collapsed ? "mt-1 space-y-1 border-l border-white/5 ml-3 pl-0" : "mt-1 space-y-1 ml-2 border-l border-white/5 pl-2"} data-testid={`nav-group-${group.key}`}>
                  {group.items.map((item) => (
                    <NavItem
                      key={item.to}
                      item={item}
                      collapsed={collapsed}
                      badges={badges}
                      loc={loc}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="mt-4 mx-auto flex items-center justify-center w-10 h-10 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
            title="Expand sidebar ( [ )"
            data-testid="sidebar-expand-btn"
          >
            <PanelLeftOpen size={16} />
          </button>
        )}
      </nav>

      <div className={`py-4 border-t ${collapsed ? "px-2" : "px-4"}`} style={{ borderColor: "var(--pm-border-dark)" }}>
        <div className={`flex items-center ${collapsed ? "flex-col gap-2" : "gap-3"}`}>
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={user?.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold shrink-0">
              {user?.name?.charAt(0) || "U"}
            </div>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{user?.role}</div>
            </div>
          )}
          <button
            onClick={logout}
            className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5"
            data-testid="sidebar-logout-btn"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
      </aside>
    </>
  );
}
